import { Button, Modal, ModalBody, ModalContent } from '@heroui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetRecoilState } from 'recoil';
import toast from 'react-hot-toast';
import { FaTrash } from 'react-icons/fa6';
import repositories from '../../repositories.jsx';
import { autoDubWorkflowsState } from '../../store.jsx';

/* Confirmation modal for deleting an Auto-Dub workflow. Mirrors widgets/DeleteModal
   (project delete), with an extra warning when dubs are still in flight — deleting
   best-effort cancels those Prefect runs on the backend. */
export default function DeleteWorkflowModal({ wf, isOpen, onClose }) {
	const queryClient = useQueryClient();
	const setWorkflows = useSetRecoilState(autoDubWorkflowsState);

	// stats.dubbing already folds 'dubbing' + 'publishing' (see API _workflow_stats).
	const inFlight = wf?.stats?.dubbing || 0;

	const onDelete = useMutation({
		mutationFn: () => repositories.autoDub.workflows.remove(wf.id),
		onSuccess: async () => {
			setWorkflows(prev => prev.filter(w => w.id !== wf.id));
			await queryClient.invalidateQueries({ queryKey: ['autoDubWorkflows'] });
			toast.success('Workflow deleted.');
			onClose();
		},
		onError: async (error) => {
			// Already gone (e.g. deleted in another tab) — reconcile and dismiss.
			await queryClient.invalidateQueries({ queryKey: ['autoDubWorkflows'] });
			toast.error(error?.response?.data?.detail || 'Could not delete workflow. Please try again.');
		},
	});

	return (
		<Modal
			classNames={{
				base: 'overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl',
				body: 'p-0',
				backdrop: 'bg-gradient-to-t from-zinc-900 to-zinc-900/10 backdrop-opacity-20 backdrop-blur-sm',
			}}
			size="lg"
			hideCloseButton
			isOpen={isOpen}
			onOpenChange={(open) => { if (!open) onClose(); }}>
			<ModalContent>
				<ModalBody>
					{/* Hero band — danger themed with a pulsing trash badge */}
					<div className="relative overflow-hidden bg-gradient-to-br from-[#ef4444] via-[#f05252] to-[#fb7185] px-8 pt-9 pb-12">
						<div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
						<div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

						<div className="relative flex flex-col items-center text-center">
							<div className="relative grid h-16 w-16 place-items-center">
								<span className="absolute inset-0 rounded-2xl bg-white/30 animate-ping" />
								<span className="relative grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
									<FaTrash className="text-2xl text-white" />
								</span>
							</div>
							<h2 className="mt-4 text-xl font-semibold text-white">Delete this workflow?</h2>
							<p className="mt-1 max-w-sm text-sm font-light text-white/80">
								This action is permanent and can&apos;t be undone.
							</p>
						</div>
					</div>

					{/* Lifted workflow card */}
					<div className="relative -mt-6 px-6">
						<div className="flex items-center gap-3 rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4 shadow-sm">
							<div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#ef44441a] text-[#ef4444]">
								<i className="hgi hgi-stroke hgi-workflow-square-10 text-lg" />
							</div>
							<div className="min-w-0">
								<p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Workflow</p>
								<p className="truncate text-sm font-semibold text-slate-800">{wf?.name}</p>
							</div>
						</div>
					</div>

					{/* Consequence note */}
					<div className="px-6 pt-4">
						{inFlight > 0 ? (
							<div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
								<i className="hgi hgi-stroke hgi-alert-02 mt-0.5 text-lg text-amber-500" />
								<p className="text-sm font-light leading-relaxed text-amber-700">
									<strong className="font-semibold">{inFlight} dub{inFlight === 1 ? '' : 's'} in progress</strong> will be cancelled.
									Videos already published stay on YouTube.
								</p>
							</div>
						) : (
							<div className="flex items-start gap-3 rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4">
								<i className="hgi hgi-stroke hgi-information-circle mt-0.5 text-lg text-slate-400" />
								<p className="text-sm font-light leading-relaxed text-slate-500">
									This removes the workflow and its entire queue.
								</p>
							</div>
						)}
					</div>

					{/* Actions */}
					<div className="flex justify-end gap-3 px-6 py-5">
						<Button
							onPress={onClose}
							variant="bordered"
							className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700">
							Keep it
						</Button>
						<Button
							isLoading={onDelete.isPending}
							onPress={() => onDelete.mutate()}
							className="h-11 rounded-xl bg-[#ef4444] px-6 text-sm font-medium text-white shadow-lg shadow-[#ef444440] transition hover:opacity-95">
							<FaTrash className="text-[13px]" /> Yes, delete
						</Button>
					</div>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
